import { Card } from "@/components/ui/card";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { format, parseISO, addMonths, subMonths } from "date-fns";
import { TrendingUp } from "lucide-react";

interface Invoice {
  id: string;
  payment_amount: number;
  discounted_payment_amount: number | null;
  discount_amount: number | null;
  status: string;
  created_at: string;
  payment_due_date: string;
}

interface InvoiceLineChartProps {
  invoices: Invoice[];
  historicalInvoices?: Invoice[];
}

export function InvoiceLineChart({ invoices, historicalInvoices = [] }: InvoiceLineChartProps) {
  const today = new Date();

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  // Merge real + historical invoices
  const realIds = new Set(invoices.map((i) => i.id));
  const allInvoices = [...invoices, ...historicalInvoices.filter((h) => !realIds.has(h.id))];

  // Build monthly data from past 4 months through future 3 months
  const months: { date: Date; key: string }[] = [];
  for (let i = -4; i <= 3; i++) {
    const d = i < 0 ? subMonths(today, Math.abs(i)) : addMonths(today, i);
    months.push({ date: d, key: format(d, "yyyy-MM") });
  }

  // Cumulative calculations: always output values for past months so lines connect
  let cumulativeTotal = 0;
  let cumulativeAccelerated = 0;
  let cumulativeProjected = 0;

  const chartDataRaw = months.map(({ date, key }) => {
    const isFuture = date > today;
    const monthInvoices = allInvoices.filter((inv) => {
      const invDate = parseISO(isFuture ? inv.payment_due_date : inv.created_at);
      return format(invDate, "yyyy-MM") === key;
    });

    const totalAmount = monthInvoices.reduce((s, i) => s + i.payment_amount, 0);
    const acceleratedAmount = monthInvoices
      .filter(
        (i) =>
          i.status === "early_payment_scheduled" ||
          i.status === "early_payment_completed" ||
          i.status === "early_payment" ||
          i.status === "paid"
      )
      .reduce((s, i) => s + (i.discounted_payment_amount || i.payment_amount), 0);

    cumulativeTotal += totalAmount;
    cumulativeAccelerated += acceleratedAmount;

    if (isFuture) {
      // Project 1-2% discount on future invoices (pay 98.5% of total)
      const projectedPayment = totalAmount > 0 ? totalAmount * 0.985 : 0;
      cumulativeProjected = (cumulativeProjected || cumulativeAccelerated) + projectedPayment;
    }

    return {
      label: format(date, "MMM yy"),
      key,
      cumulativeTotal: cumulativeTotal ?? 0,
      cumulativeAccelerated: !isFuture ? cumulativeAccelerated : null,
      cumulativeProjected: isFuture ? cumulativeProjected : null,
      isFuture,
    };
  });

  // Bridge: set projected on the last past month = last accelerated so the dashed line connects
  const lastPastIdx = chartDataRaw.findIndex((r) => r.isFuture);
  const chartData =
    lastPastIdx > 0
      ? chartDataRaw.map((row, i) =>
          i === lastPastIdx - 1 ? { ...row, cumulativeProjected: row.cumulativeAccelerated } : row
        )
      : chartDataRaw;

  const todayLabel = format(today, "MMM yy");

  return (
    <Card className="glass-card p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-lg gradient-primary flex items-center justify-center shadow-primary">
          <TrendingUp className="w-4 h-4 text-primary-foreground" />
        </div>
        <div>
          <h3 className="font-display font-semibold text-foreground text-sm tracking-tight">
            Cumulative Invoice & Savings Trend
          </h3>
          <p className="text-[11px] text-muted-foreground">
            Running total of invoice volume and early payments over time
          </p>
        </div>
      </div>
      <div className="mb-4 rounded-md bg-muted/50 border border-border/50 px-3 py-2 text-[11px] text-muted-foreground space-y-1">
        <p className="font-medium text-foreground/90">What you&apos;re seeing</p>
        <ul className="list-disc list-inside space-y-0.5 ml-0.5">
          <li>
            <strong className="text-foreground/80">Total Invoiced</strong> — All invoice volume
            received (cumulative).
          </li>
          <li>
            <strong className="text-foreground/80">Accelerated (Actual)</strong> — Invoices paid
            early with DiscoRun AI; the gap below Total is your discount savings.
          </li>
          <li>
            <strong className="text-foreground/80">Projected with DiscoRun AI</strong> — Estimated
            cumulative early payments if upcoming invoices are paid early (dashed).
          </li>
        </ul>
      </div>

      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.15} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="accelGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--success))" stopOpacity={0.15} />
              <stop offset="95%" stopColor="hsl(var(--success))" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
          <YAxis
            tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`}
            tick={{ fontSize: 10 }}
            stroke="hsl(var(--muted-foreground))"
          />
          <Tooltip
            formatter={(value: number, name: string) => [
              formatCurrency(value),
              name === "cumulativeTotal"
                ? "Total Invoiced"
                : name === "cumulativeAccelerated"
                  ? "Accelerated (Actual)"
                  : "Projected with DiscoRun AI",
            ]}
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              fontSize: "11px",
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: "11px" }}
            formatter={(value) =>
              value === "cumulativeTotal"
                ? "Total Invoiced"
                : value === "cumulativeAccelerated"
                  ? "Accelerated (Actual)"
                  : "Projected Savings with DiscoRun AI"
            }
          />
          <ReferenceLine
            x={todayLabel}
            stroke="hsl(var(--muted-foreground))"
            strokeDasharray="4 4"
            label={{
              value: "Today",
              position: "top",
              fontSize: 9,
              fill: "hsl(var(--muted-foreground))",
            }}
          />
          <Area
            type="monotone"
            dataKey="cumulativeTotal"
            stroke="hsl(var(--primary))"
            strokeWidth={2}
            fill="url(#totalGradient)"
            dot={{ r: 3, fill: "hsl(var(--primary))" }}
            connectNulls
          />
          <Area
            type="monotone"
            dataKey="cumulativeAccelerated"
            stroke="hsl(var(--success))"
            strokeWidth={2}
            fill="url(#accelGradient)"
            dot={{ r: 3, fill: "hsl(var(--success))" }}
            connectNulls
          />
          <Area
            type="monotone"
            dataKey="cumulativeProjected"
            stroke="hsl(var(--success))"
            strokeWidth={2}
            strokeDasharray="6 3"
            fill="none"
            dot={{ r: 2, fill: "hsl(var(--success))", strokeDasharray: "" }}
            connectNulls
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}
